<script lang="ts">
  import { onMount } from 'svelte';
  import { fly } from 'svelte/transition';
  import { UPGRADES } from '../core/balance';
  import { medals, purchaseStatus } from '../core/engine';
  import Icon, { type IconName } from '../shared/components/Icon.svelte';
  import Toasts from '../shared/components/Toasts.svelte';
  import { sound } from '../shared/lib/audio';
  import { startFeedback } from '../shared/lib/feedback';
  import { fmt, fmtShort } from '../shared/lib/format';
  import { game } from '../shared/lib/game.svelte';
  import { settings } from '../shared/lib/settings.svelte';
  import Collection from './views/Collection.svelte';
  import Play from './views/Play.svelte';
  import Records from './views/Records.svelte';
  import Settings from './views/Settings.svelte';
  import Upgrades from './views/Upgrades.svelte';

  type Tab = 'play' | 'upgrades' | 'collection' | 'records' | 'settings';
  const TABS: Array<{ id: Tab; label: string; icon: IconName }> = [
    { id: 'play', label: 'Walk', icon: 'walk' },
    { id: 'upgrades', label: 'Upgrades', icon: 'upgrades' },
    { id: 'collection', label: 'Collection', icon: 'medal' },
    { id: 'records', label: 'Records', icon: 'records' },
    { id: 'settings', label: 'Settings', icon: 'settings' },
  ];
  const isTab = (x: string): x is Tab => TABS.some((t) => t.id === x);

  let tab = $state<Tab>(isTab(location.hash.slice(1)) ? (location.hash.slice(1) as Tab) : 'play');
  let main: HTMLElement;
  let pointsEl: HTMLElement;

  function go(next: Tab) {
    if (next === tab) return;
    tab = next;
    main?.scrollTo({ top: 0 });
    history.replaceState(
      null,
      '',
      next === 'play' ? location.pathname + location.search : `#${next}`,
    );
  }

  const s = $derived(game.s);
  const canBuy = $derived(
    UPGRADES.some((u) => {
      const st = purchaseStatus(s, u.id);
      return st.kind === 'available' && st.affordable;
    }),
  );

  // Settings → document, audio and storage.
  $effect(() => {
    document.documentElement.dataset.palette = settings.palette;
    document.documentElement.classList.toggle('reduce-motion', settings.reducedMotion);
    sound.enabled = settings.sound;
    sound.setVolume(settings.volume);
    settings.persist();
  });

  // A small lift on the points counter whenever a bonus lands.
  let lastPoints = game.s.points;
  $effect(() => {
    const p = s.points;
    if (p - lastPoints >= 5 && pointsEl && !settings.reducedMotion) {
      pointsEl.animate([{ transform: 'translateY(-3px) scale(1.08)' }, { transform: 'none' }], {
        duration: 380,
        easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
      });
    }
    lastPoints = p;
  });

  function onKey(e: KeyboardEvent) {
    if (tab !== 'play' || e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
    const el = e.target as HTMLElement;
    if (el.closest('input, textarea, select, button, [role="radio"]')) return;
    if (e.code === 'Space' || e.code === 'Enter') {
      e.preventDefault();
      sound.unlock();
      game.tap();
    } else if (e.key === 'f' || e.key === 'F') {
      game.flip();
    }
  }

  onMount(() => {
    const stopFeedback = startFeedback();
    let raf = 0;
    let prev = performance.now();
    const loop = (now: number) => {
      game.frame(now - prev, now);
      prev = now;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const flush = () => game.save();
    const onVisibility = () => document.visibilityState === 'hidden' && flush();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);

    return () => {
      cancelAnimationFrame(raf);
      stopFeedback();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  });
</script>

<svelte:window onkeydown={onKey} />

<div class="shell">
  <header class="bar">
    <div class="brand">
      <span class="mark" aria-hidden="true">ℤ</span>
      <span class="name">Integerney</span>
    </div>

    <nav class="tabs-top" aria-label="Main">
      {#each TABS as t (t.id)}
        <button
          class:on={tab === t.id}
          aria-current={tab === t.id ? 'page' : undefined}
          onclick={() => go(t.id)}
        >
          {t.label}
          {#if t.id === 'upgrades' && canBuy}<span class="dot" aria-hidden="true"></span><span
              class="sr-only">, upgrade available</span
            >{/if}
        </button>
      {/each}
    </nav>

    <div class="wallet">
      <span class="stat" title="Prime medals">
        <span class="sym prime" aria-hidden="true"></span>
        <span class="num">{fmtShort(medals(s))}</span>
        <span class="sr-only">prime medals</span>
      </span>
      <span class="stat points" title="{fmt(s.points)} points">
        <span class="num" bind:this={pointsEl}>{fmtShort(s.points)}</span>
        <span class="unit">pts</span>
      </span>
    </div>
  </header>

  <main bind:this={main} class:locked={tab === 'play'}>
    {#key tab}
      <div class="page" in:fly={{ y: 10, duration: settings.reducedMotion ? 0 : 240, opacity: 0 }}>
        {#if tab === 'play'}
          <Play onNavigate={go} />
        {:else if tab === 'upgrades'}
          <Upgrades />
        {:else if tab === 'collection'}
          <Collection />
        {:else if tab === 'records'}
          <Records />
        {:else}
          <Settings />
        {/if}
      </div>
    {/key}
  </main>

  <nav class="tabs-bottom" aria-label="Main">
    {#each TABS as t (t.id)}
      <button
        class:on={tab === t.id}
        aria-current={tab === t.id ? 'page' : undefined}
        onclick={() => go(t.id)}
      >
        <span class="ico">
          <Icon name={t.icon} size={22} />
          {#if t.id === 'upgrades' && canBuy}<span class="dot" aria-hidden="true"></span>{/if}
        </span>
        <span class="lbl">{t.label}</span>
        {#if t.id === 'upgrades' && canBuy}<span class="sr-only">, upgrade available</span>{/if}
      </button>
    {/each}
  </nav>
</div>

<Toasts />

<style>
  .shell {
    height: 100%;
    display: grid;
    grid-template-rows: auto 1fr auto;
  }

  .bar {
    position: relative;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    min-height: calc(var(--header-h) + env(safe-area-inset-top, 0px));
    padding: env(safe-area-inset-top, 0px) var(--space-4) 0;
    border-bottom: 1px solid color-mix(in srgb, var(--line) 70%, transparent);
    background: color-mix(in srgb, var(--bg) 85%, transparent);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }

  .brand {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .mark {
    width: 30px;
    height: 30px;
    display: grid;
    place-items: center;
    border-radius: 8px;
    font-family: var(--font-num);
    font-weight: 700;
    font-size: 1.15rem;
    color: var(--on-accent);
    background: var(--accent);
  }
  .name {
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  .wallet {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .stat {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border-radius: 999px;
    background: var(--bg-sunken);
    font-weight: 700;
    font-size: var(--text-sm);
  }
  .stat .num {
    display: inline-block;
  }
  .sym.prime {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--prime);
  }
  .points {
    color: var(--accent);
  }
  .points .unit {
    font-size: var(--text-xs);
    color: var(--ink-3);
    font-weight: 600;
  }

  main {
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }
  main.locked {
    overflow: hidden;
  }
  .page {
    min-height: 100%;
    height: 100%;
  }
  main:not(.locked) .page {
    height: auto;
  }

  .dot {
    position: absolute;
    top: -2px;
    right: -6px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 0 2px var(--bg);
  }

  .tabs-bottom {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    padding: 6px 4px calc(6px + env(safe-area-inset-bottom, 0px));
    border-top: 1px solid color-mix(in srgb, var(--line) 70%, transparent);
    background: color-mix(in srgb, var(--bg) 88%, transparent);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }
  .tabs-bottom button {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 4px 0;
    color: var(--ink-3);
    font-size: 11px;
    font-weight: 600;
    transition: color var(--t) var(--ease-out);
  }
  .tabs-bottom button.on {
    color: var(--accent);
  }
  .tabs-bottom .ico {
    position: relative;
    display: grid;
    place-items: center;
    width: 48px;
    height: 28px;
    border-radius: 999px;
    transition: background-color var(--t) var(--ease-out);
  }
  .tabs-bottom button.on .ico {
    background: color-mix(in srgb, var(--accent) 14%, transparent);
  }
  .tabs-bottom .dot {
    top: 2px;
    right: 8px;
  }

  .tabs-top {
    display: none;
  }

  @media (min-width: 860px) {
    .tabs-bottom {
      display: none;
    }
    .tabs-top {
      display: flex;
      gap: 2px;
      padding: 3px;
      border-radius: 999px;
      background: var(--bg-sunken);
    }
    .tabs-top button {
      position: relative;
      padding: 6px 14px;
      border-radius: 999px;
      font-size: var(--text-sm);
      font-weight: 600;
      color: var(--ink-2);
      transition:
        background-color var(--t) var(--ease-out),
        color var(--t) var(--ease-out);
    }
    .tabs-top button:hover {
      color: var(--ink);
    }
    .tabs-top button.on {
      background: var(--bg-raised);
      color: var(--ink);
      box-shadow: 0 1px 3px rgb(0 0 0 / 0.12);
    }
    .tabs-top .dot {
      top: 4px;
      right: 4px;
    }
  }

  @media (max-width: 380px) {
    .name {
      display: none;
    }
  }
</style>
